import { useCallback, useContext, useEffect, useState } from 'react';
import { SiteSettingsContext, useSportsSettings } from '@namespace/cms';
import { STREAM_TYPE } from '@namespace/common';
import { useDeviceStore } from '@namespace/device';
import { useI18n } from '@namespace/i18n';
import { USER_ROLES, UserContext } from '@namespace/user';
import { IS_WIDGET, WidgetContext } from '@namespace/widget';
import { ONE_EVENT_HEADER_TABS as TABS } from '../../constants';
import { sportsbookActions, useEventFields } from '../../store';
import { Event, useVideoStreamInfo } from '../useVideoStreamInfo';
import { useRedirectToLogin } from './hooks/useRedirectToLogin';

const {
  SET_EVENT_DATA,
  FETCH_ACTIVE_EVENT_STREAM,
  FETCH_BROADCAST_SOURCE_URL
} = sportsbookActions;

export type TabState = {
  type: string | null;
  opened: boolean;
};

type Settings = {
  partnerId: number;
};

export const useVideoStream = (
  eventId: number,
  tabState: TabState,
  setTabState: (tabState: TabState) => void,
  isShowStreamForGuest: boolean
) => {
  const [isReady, setIsReady] = useState(false);
  const { broadcast, eventTv, eventBroadcastUrl, streamUrl } = useEventFields({
    eventId,
    fieldPath: undefined,
    fields: ['broadcast', 'eventTv', 'eventBroadcastUrl', 'streamUrl']
  }) as Event;
  const [user, userActions] = useContext(UserContext);
  const { id: userId, countryId, role } = user;
  const { CHECK_IS_VIDEO_AVAILABLE } = userActions;
  const [settings]: [Record<string, unknown>] = useContext(SiteSettingsContext);
  const { siteConfig = {} } = settings;
  const { partnerId } = siteConfig as Settings;
  const { language: lang } = useI18n();
  const [errorMessage, setErrorMessage] = useState('');
  const { isDesktopDevice } = useDeviceStore();
  const [{ parentHostname }] = useContext(WidgetContext);
  const { showTwitchInNewTab } = useSportsSettings();
  const isLoggedIn = role === USER_ROLES.USER;

  const { isShowVideo, isVideoFree, streamType, streamPlayerType } =
    useVideoStreamInfo({ eventId });

  const checkUserRole = useRedirectToLogin(isShowStreamForGuest);

  const isFavTvAvailableForCountry =
    IS_WIDGET || Boolean(eventTv?.countries?.includes(countryId));

  const expandVideo = useCallback(async () => {
    // close video tab
    if (tabState.type === TABS.VIDEO) {
      setTabState({ type: null, opened: false });
      return;
    }

    // check user role
    if (!checkUserRole()) {
      return;
    }

    const preventOpenEventTab =
      showTwitchInNewTab && streamType === STREAM_TYPE.TWITCH;
    if (!preventOpenEventTab) {
      setTabState({ type: TABS.VIDEO, opened: true });
    }

    switch (streamType) {
      case STREAM_TYPE.FAVTV:
        if (!isFavTvAvailableForCountry) {
          setErrorMessage('sportsbook.events.stream.noCountry');
          break;
        }
      // eslint-disable-next-line no-fallthrough
      case STREAM_TYPE.BROADCAST: {
        if (!isVideoFree && !IS_WIDGET) {
          try {
            const isPaidStreamAllowed = await CHECK_IS_VIDEO_AVAILABLE(userId);
            if (!isPaidStreamAllowed) {
              setErrorMessage('sportsbook.events.stream.paidStream');
            }
          } catch (error: unknown) {
            setErrorMessage((error as { errorCode: string }).errorCode);
          }
        }
        break;
      }

      case STREAM_TYPE.TWITCH: {
        if (showTwitchInNewTab) {
          console.log('test');
          window.open(streamUrl, '_blank', 'noopener,noreferrer');
          return;
        }
        break;
      }

      default:
        break;
    }

    setIsReady(true);
  }, [
    tabState.type,
    checkUserRole,
    showTwitchInNewTab,
    streamType,
    setTabState,
    isFavTvAvailableForCountry,
    isVideoFree,
    CHECK_IS_VIDEO_AVAILABLE,
    userId,
    streamUrl
  ]);

  useEffect(() => {
    if (!isShowVideo) {
      return;
    }

    if (streamUrl || errorMessage) {
      return;
    }

    try {
      switch (streamType) {
        case STREAM_TYPE.BROADCAST: {
          if (!isLoggedIn) {
            break;
          }

          FETCH_BROADCAST_SOURCE_URL({
            partnerId,
            eventId,
            userId,
            streamId: broadcast,
            isDesktopDevice
          });
          break;
        }

        case STREAM_TYPE.TWITCH: {
          const decodedUrl = decodeURIComponent(eventBroadcastUrl);
          if (URL.canParse(decodedUrl)) {
            const srcUrl = new URL(decodedUrl);
            if (!showTwitchInNewTab) {
              srcUrl.searchParams.append('parent', window.location.hostname);
              if (IS_WIDGET && parentHostname) {
                srcUrl.searchParams.append('parent', parentHostname);
              }
            }
            SET_EVENT_DATA(eventId, { streamUrl: srcUrl.toString() });
          } else {
            setErrorMessage('sportsbook.events.stream.crash');
          }
          break;
        }

        case STREAM_TYPE.FAVTV: {
          FETCH_ACTIVE_EVENT_STREAM({ eventId, partnerId, lang });
          break;
        }

        default:
          break;
      }
    } catch (ex) {
      setErrorMessage(ex as string);
    }
  }, [
    broadcast,
    errorMessage,
    eventBroadcastUrl,
    eventId,
    isDesktopDevice,
    isLoggedIn,
    isShowVideo,
    lang,
    parentHostname,
    partnerId,
    showTwitchInNewTab,
    streamType,
    streamUrl,
    userId
  ]);

  return {
    isReady,
    streamUrl,
    streamPlayerType,
    errorMessage,
    isShowVideo,
    isVideoFree,
    expandVideo
  };
};
